import { Fragment, useCallback } from 'react';

import { useListConsumerFamilies } from '@givebacks/api/backer';
import { Avatar, Box, Divider, Modal, Stack, Text, ThemeIcon } from 'ui';
import { IconAdd } from 'ui/Icons';

import { ClickableOptionsCard } from '../../../../Cards/ClickableOptionsCard/ClickableOptionsCard';
import {
  ModalTitleWithBackButton,
  useDirectorySettingsModalProvider,
} from '../DirectorySettingsModalProvider';

const ManageFamiliesModalContent = () => {
  const { openModalById, setMeta } = useDirectorySettingsModalProvider({});
  const { data: families } = useListConsumerFamilies({ include: 'backers,children' });

  const handleFamilyClick = useCallback(
    (familyId: string) => {
      setMeta({
        familyId,
      });
      openModalById('members');
    },
    [openModalById, setMeta]
  );

  return (
    <Stack gap="lg">
      <Text fz="xs" c="gray">
        Manage family members you’d like to display in your organizations’ directories.
      </Text>
      <Stack gap={4}>
        <Text fw={600} fz="lg">
          Families
        </Text>
        <Box>
          {families?.map((family) => (
            <Fragment key={family.uuid}>
              <ClickableOptionsCard.Row
                leftSection={<Avatar size={48} color="cobalt" name={family.name} ml={-16} />}
                title={`${family.name} Family`}
                subtitle={[
                  family.backers?.map((adult) => adult.first_name).join(', '),
                  family.children?.map((child) => child.first_name).join(', '),
                ]
                  .filter((val) => val)
                  .join(', ')}
                onClick={() => handleFamilyClick(family.uuid)}
              />
              <Divider color="gray.0" size="sm" />
            </Fragment>
          ))}
          <ClickableOptionsCard.Row
            leftSection={
              <ThemeIcon variant="transparent" color="cobalt">
                <IconAdd />
              </ThemeIcon>
            }
            title="Add Family"
            onClick={() => openModalById('family')}
            rightSection={<></>}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

const ManageFamiliesModal = () => {
  const { opened, close } = useDirectorySettingsModalProvider({
    id: 'families',
    onOpened: ({ resetMeta }) => {
      resetMeta();
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={<ModalTitleWithBackButton>Manage Family</ModalTitleWithBackButton>}
    >
      <ManageFamiliesModalContent />
    </Modal>
  );
};

export { ManageFamiliesModal };
